/* eslint-disable react/jsx-no-bind */
import { useCallback, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { isEmpty, prop, sortBy } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import OutputTextField from 'components/atoms/GefMui/OutputTextField';
import Panel from 'components/atoms/GefMui/Panel/Panel';
import Subheader from 'components/atoms/GefMui/Subheader';
import { HtmlOutput } from 'components/atoms/HtmlOutput';
import { defaultDateFormatDateFns } from 'constants/general';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import taskDetail from 'features/views/taskDetail';
import { equalOrThreeDashes, fromIsoDate } from 'utils';
import { getStreetWithNumber } from 'utils/address';
import { getClientName } from 'utils/client';

export const PrintNein = () => {
	const dispatch = useDispatch();
	const { mode } = useColorScheme();

	const taskData = useSelector(taskDetail.selectors.getDetail);
	const policyNo = useSelector(taskDetail.selectors.getPolicyNo);

	const { client = {}, metadata = {}, solver = {}, policy = {}, comments = [], id } = taskData;
	const { common = {} } = metadata;
	const { address = {} } = client;

	// 'thing' value === the oldest / first Comment
	const oldestComment = useMemo(() => {
		const sortByTime = sortBy(prop('dateCreated'));
		const sortedComments = sortByTime(comments);
		const content = sortedComments[0]?.comment ?? equalOrThreeDashes();
		return <HtmlOutput content={content} />;
	}, [comments]);

	const handleOpenPrintWindow = useCallback(() => {
		window.print();
	}, []);

	useEffect(() => {
		if (isEmpty(taskData)) {
			dispatch(taskDetail.actions.fetchDetails(id));
		} else {
			setTimeout(() => {
				handleOpenPrintWindow();
			}, 1500);
		}
	}, [dispatch, taskData, handleOpenPrintWindow, id]);

	const salesRepresentative =
		solver && solver.sellerIn ? `${solver.sellerIn} / ${solver.surname || ''} ${solver.firstName || ''}` : '---';
	const name = getClientName(client);

	const dataGeneral = [
		{ label: 'printNein:salesRepresentative', value: salesRepresentative, size: 6 },
		{ label: 'printNein:printDate', value: format(new Date(), defaultDateFormatDateFns), size: 6 },
		{ label: 'printNein:taskId', value: id, size: 6 },
		{ label: 'printNein:dateTaskCreated', value: fromIsoDate(taskData.dateCreated), size: 6 },
	];
	const dataInsurance = [
		{ label: 'printNein:nameInsurer', value: name, size: 12 },
		{ label: 'printNein:street', value: getStreetWithNumber(address.street, address.streetNo), size: 6 },
		{ label: 'printNein:city', value: address.municipality, size: 4 },
		{ label: 'printNein:PSC', value: address.zipCode, size: 2 },
		{ label: 'printNein:policyType', value: common.rate, size: 6 },
		{ label: 'printNein:policyNumber', value: policyNo, size: 3 },
		{
			label: 'printNein:dateDealInsurance',
			value: policy.policyOpenDate ? fromIsoDate(policy.policyOpenDate) : fromIsoDate(common.openDate),
			size: 3,
		},
	];

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: `${getLabelMessage('printNein:headerSection')} ${equalOrThreeDashes(policyNo)}`,
		backButton: true,
	});

	return (
		<Panel screenId={PAGES.taskDetail.printNein.id} screenName={PAGES.taskDetail.printNein.name}>
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<Subheader title={subheaderTitle} />
					<ComponentLoader fetchStorePath={fetch.constants.urls.taskDetail} renderOnlyIfLoaded>
						<Grid container>
							<Grid size={12}>
								<Grid container spacing={2}>
									{dataGeneral.map((item, i) => (
										<Grid key={i} size={item.size}>
											<OutputTextField
												size="small"
												id={getLabelMessage(item.label)}
												label={getLabelMessage(item.label)}
												fullWidth
												value={equalOrThreeDashes(item.value)}
												slotProps={{
													inputLabel: {
														shrink: !!equalOrThreeDashes(item.value),
													},
												}}
											/>
										</Grid>
									))}
									<Grid size={12} />
									{dataInsurance.map((item, i) => (
										<Grid key={i} size={item.size}>
											<OutputTextField
												size="small"
												id={getLabelMessage(item.label)}
												label={getLabelMessage(item.label)}
												fullWidth
												value={equalOrThreeDashes(item.value)}
												slotProps={{
													inputLabel: {
														shrink: !!equalOrThreeDashes(item.value),
													},
												}}
											/>
										</Grid>
									))}
									<Grid size={12}>
										<Stack spacing={0.5}>
											<Typography
												variant="caption"
												sx={{
													color:
														isNilOrEmpty(mode) || mode === 'light'
															? 'grey.600'
															: 'grey.400',
												}}
											>
												{getLabelMessage('printNein:thing')}
											</Typography>
											<Stack
												sx={(theme) => ({
													borderRadius: '4px',
													border: `1px solid ${
														isNilOrEmpty(mode) || mode === 'light'
															? theme.palette.grey[300]
															: theme.palette.grey[700]
													}`,
													px: '14px',
													py: '8px',
													fontSize: '1rem',
												})}
											>
												{oldestComment}
											</Stack>
										</Stack>
									</Grid>
									<Grid size={6}>
										<OutputTextField
											size="small"
											id="assigner"
											label={getLabelMessage('printNein:assigner')}
											fullWidth
											value={equalOrThreeDashes(common.kdpOperatorName)}
											slotProps={{
												inputLabel: {
													shrink: !!equalOrThreeDashes(common.kdpOperatorName),
												},
											}}
										/>
									</Grid>
									<Grid size={6}>
										<OutputTextField
											size="small"
											id="dateToAssign"
											label={getLabelMessage('printNein:dateToAssign')}
											fullWidth
											value={fromIsoDate(taskData.deadlineDate)}
											slotProps={{
												inputLabel: {
													shrink: !!taskData?.deadlineDate,
												},
											}}
										/>
									</Grid>
									<Grid size={12}>
										<Grid container>
											<Grid size={12}>{getLabelMessage('printNein:typeOfAssign')}</Grid>
											<Grid size={4}>
												<FormControlLabel
													control={<Checkbox disabled checked={false} />}
													label={getLabelMessage('printNein:doneContractContinue')}
												/>
											</Grid>
											<Grid size={4}>
												<FormControlLabel
													control={<Checkbox disabled checked={false} />}
													label={getLabelMessage('printNein:doneContractCancel')}
												/>
											</Grid>
											<Grid size={4}>
												<FormControlLabel
													control={<Checkbox disabled checked={false} />}
													label={getLabelMessage('printNein:doneNein')}
												/>
											</Grid>
										</Grid>
									</Grid>
									<Grid size={12}>
										<Typography variant="h6" color="text.primary">
											{getLabelMessage('printNein:reasonCancelation')}
										</Typography>
									</Grid>
									<Grid size={12}>
										<OutputTextField size="small" multiline minRows={4} id="note" fullWidth />
									</Grid>
									<Grid size={12}>
										<Grid
											container
											sx={{
												justifyContent: 'flex-end',
											}}
										>
											<Grid size={4}>
												<Button
													id="printpagebutton"
													variant="contained"
													onClick={handleOpenPrintWindow}
													fullWidth
												>
													{getLabelMessage('common:print')}
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</ComponentLoader>
				</Stack>
			</Paper>
		</Panel>
	);
};

export default PrintNein;
